import React, { useContext, useEffect, useMemo, useState } from "react";
import { Container, Button, Row } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import SectionCard from "Components/Common/SectionCard";
import DetailField from "Components/Common/DetailField";
import { BreadcrumbContext } from "Layouts/Header";
import { useSubjectCheckin } from "../api/useSubjectCheckin";
import { useSubjectCheckout } from "../api/useSubjectCheckout";
import { useCheckListMasterService } from "modules/Library/Checklist/api/useChecklist";
import { useVolunteerService } from "modules/Volunteer/MasterVolunteer/api/useVolunteerQuery";
import { loadCodelistsDetailId } from "api/modules/common.option";
import { formatDate, formatTime24Hour } from "common/service/CommonService";
import FullPageLoader from "Components/Common/FullPageLoader";
import { STUDY_PATHS, SUBJECT_CHECKOUT_PATHS } from "Routes/paths";

const ViewSubjectCheckout: React.FC = () => {
  document.title = "View Subject Check-Out | ZENTIXS VISTA";
  
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const breadcrumbContext = useContext(BreadcrumbContext);

  const [codelistsLoaded, setCodelistsLoaded] = useState(false);

  // Fetch subject check-out data
  const { SelectQuery: CheckoutSelectQuery } = useSubjectCheckout({
    subjectCheckoutId: Number(id),
  });

  const rawCheckout = CheckoutSelectQuery.data?.data?.[0];

  // Optional: fetch linked check-in if FK present
  const linkedCheckinId = rawCheckout?.subjectcheckinid || null;
  const { SelectQuery: CheckinSelectQuery } = useSubjectCheckin({
    subjectCheckinId: linkedCheckinId,
  });
  const rawCheckin = CheckinSelectQuery.data?.data?.[0];

  const volunteerId = rawCheckout
    ? Number(rawCheckout._volunteerid || rawCheckout.volunteerid) || 0
    : 0;

  // Fetch check-out checklist questions
  const { SelectAllQuery: ChecklistQuery } = useCheckListMasterService({
    ispagination: false,
    checklisttype: "Subject Check-Out",
  });

  const checkoutQuestions = useMemo(() => {
    return ChecklistQuery?.data?.data ?? [];
  }, [ChecklistQuery?.data?.data]);

  // Fetch volunteer details
  const { SelectQuery: VolunteerSelectQuery } = useVolunteerService({
    id: volunteerId,
  });
  const volunteerDetails = VolunteerSelectQuery?.data?.data?.[0] || null;

  // Load codelists for checklists
  useEffect(() => {
    const load = async () => {
      await loadCodelistsDetailId(["Checklist_Type_OPTIONS"]);
      setCodelistsLoaded(true);
    };
    load();
  }, []);

  // Sync breadcrumbs
  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Study", link: STUDY_PATHS.LIST },
      { text: "Check-Out", link: SUBJECT_CHECKOUT_PATHS.LIST },
      { text: "View Check-Out Details" },
    ]);

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, []);

  // Resolve normalized record data
  const data = useMemo(() => {
    if (!rawCheckout) return null;
    const normalized: Record<string, any> = {};
    Object.keys(rawCheckout).forEach((key) => {
      const normalizedKey = key.startsWith("_") ? key.substring(1) : key;
      normalized[normalizedKey] = rawCheckout[key];
    });
    return normalized;
  }, [rawCheckout]);

  // Map answered checklist details
  const checkoutChecklist = useMemo(() => {
    if (!data?.checklist) return [];
    return data.checklist
      .filter((item: any) =>
        checkoutQuestions.some(
          (q: any) => String(q.masterchecklistid) === String(item.questionid),
        ),
      )
      .map((item: any) => ({
        question: item.questiontext,
        answered: item.ischecked === true || String(item.ischecked) === "true",
      }));
  }, [data?.checklist, checkoutQuestions]);

  const checkoutStatus = useMemo(() => {
    if (!data) return "Pending";
    const low = (data.checkoutstatus ?? "").trim().toLowerCase();
    if (low.includes("pending")) return "Pending";
    if (low.includes("checked") && low.includes("out")) return "Checked Out";
    if (data.checkoutdate) return "Checked Out";
    return "Pending";
  }, [data]);

  const isLoading =
    CheckoutSelectQuery.isLoading ||
    ChecklistQuery.isLoading ||
    !codelistsLoaded ||
    (volunteerId > 0 && VolunteerSelectQuery.isFetching);

  if (isLoading) {
    return (
      <div
        className="page-content d-flex align-items-center justify-content-center"
        style={{ minHeight: "80vh" }}
      >
        <FullPageLoader />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <i className="ri-error-warning-line display-3 text-warning mb-3 d-block" />
            <h4 className="fw-600">Check-Out Record Not Found</h4>
            <p className="text-muted">
              The subject check-out record you are looking for does not exist or has been removed.
            </p>
            <Button
              color="primary"
              onClick={() => navigate(SUBJECT_CHECKOUT_PATHS.LIST)}
              className="mt-3"
            >
              Back to Check-Out List
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        {/* Header Section */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h3 className="fw-700 mb-1">Subject Check-Out Details</h3>
            <p
              className="text-muted mb-0 d-flex align-items-center flex-wrap"
              style={{ gap: "8px" }}
            >
              <span>Subject Number: Subject {data.subjectno || "--"}</span>
              <span className="mx-1">|</span>
              <span>Initials: {volunteerDetails?.initials || data.initials || "--"}</span>
              <span className="mx-1">|</span>
              <span>Name: {volunteerDetails?.fullname || data.subjectname || "--"}</span>
            </p>
          </div>
          <Button
            color="primary"
            outline
            onClick={() => navigate(SUBJECT_CHECKOUT_PATHS.LIST)}
            className="fw-600"
          >
            <i className="ri-arrow-left-line me-1" /> Back to Check-Out
          </Button>
        </div>

        <div className="d-flex flex-column gap-3">
          {/* Section 1: Check-Out Details */}
          <SectionCard
            icon="ri-logout-box-r-line"
            title="Check-Out Information"
            subtitle="Details of subject clearance, checkout schedule, timing, and remarks."
          >
            <Row>
              <DetailField
                label="Check-Out Status"
                value={
                  <span
                    className={`badge px-3 py-2 rounded-pill fs-12 ${checkoutStatus === "Checked Out"
                        ? "bg-info-subtle text-info border border-info"
                        : "bg-warning-subtle text-warning border border-warning"
                      }`}
                  >
                    {checkoutStatus}
                  </span>
                }
                columns={4}
              />
              <DetailField
                label="Check-Out Date"
                value={data.checkoutdate ? formatDate(data.checkoutdate) : "--"}
                columns={4}
              />
              <DetailField
                label="Check-Out Time"
                value={data.checkouttime ? formatTime24Hour(data.checkouttime) : "--"}
                columns={4}
              />
              {checkoutStatus === "Checked Out" && (
                <DetailField
                  label="Remarks"
                  value={data.remarks}
                  columns={12}
                  expandable={true}
                  maxChars={200}
                />
              )}
            </Row>
          </SectionCard>

          {/* Section 2: Check-Out Checklist */}
          {checkoutStatus === "Checked Out" && checkoutChecklist.length > 0 && (
            <SectionCard
              icon="ri-survey-line"
              title="Clearance Checklist"
              subtitle="Safety and compliance checklist items answered during check-out."
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {checkoutChecklist.map((item: any, i: number) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "12px 16px",
                      background: "#f8fafc",
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <div
                      style={{ fontSize: "14.5px", fontWeight: 500, color: "#334155" }}
                    >
                      {item.question}
                    </div>
                    <div>
                      {item.answered ? (
                        <span className="badge bg-success-subtle text-success px-3 py-1.5 rounded-pill fs-12 border border-success">
                          <i className="ri-checkbox-circle-line me-1" /> Yes
                        </span>
                      ) : (
                        <span className="badge bg-light text-muted px-3 py-1.5 rounded-pill fs-12 border border-secondary-subtle">
                          <i className="ri-close-circle-line me-1" /> No
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}
        </div>
      </Container>
    </div>
  );
};

export default ViewSubjectCheckout;
